import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DataGrid,
  GridFooterContainer,
  gridPageCountSelector,
  // getGridStringOperators, //customize this for filter whenever API is ready for that
  useGridApiContext,
  useGridSelector,
  GridPagination,
  GridToolbar
} from '@mui/x-data-grid';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import UndoIcon from '@mui/icons-material/Undo';

import Pagination from '@mui/material/Pagination';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTheme } from '@mui/material/styles';

import SearchField from '../../../components/1elements/Fields/SearchField';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useDebounce from '../../../services/customHooks/useDebounce';
import {
  GarbageBinIconButton,
  ITooltip,
  Loader
} from '../../../components/1elements/index';
import { reExportedColors } from '../../../components/1elements';
import ThreeStageModal from '../modal/threeStageModal/ThreeStageModal';
import NoData from './NoData';
import activationSwitchStyle from './activationSwitchStyle';

/**
 * Modal default props
 */
let modalInfo = {
  type: undefined,
  pageCodeText: undefined,
  data: undefined,
  modalConfirmationFunction: undefined
};

const DEFAULT_PAGINATION = {
  /**
   * Page index
   */
  page: 0,
  per_page: 20,
  total: 0
};

const EDIT_STATUS_COLUMN_WIDTH = 300;

const CustomPagination = ({
  pageCount,
  pagination = DEFAULT_PAGINATION,
  onPageChange = () => {}
}) => {
  const currentPage = useMemo(() => pagination.page + 1, [pagination]);

  return (
    <Pagination
      count={pageCount ?? 0}
      page={currentPage}
      onChange={(_, value) => {
        // `value` returns in a human-readable way.
        // Need to subtract 1 to have the correct page index
        onPageChange(value - 1);
      }}
    />
  );
};

const CustomFooter = (props) => {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <GridFooterContainer>
      <CustomPagination {...props} pageCount={pageCount} />
      <GridPagination ActionsComponent={() => null} />
    </GridFooterContainer>
  );
};

const GenericGrid = ({
  initialFetchParameters,
  visibleColumns,
  editMode,
  fetchCallBack,
  detailCallBack,
  deleteCallBack,
  activationCallBack,
  deletionChanges,
  activationChanges,
  showWarning,
  pageCodeText,
  systemRowActionMessage,
  systemRowLabel = 'System'
}) => {
  /// initialization
  const classes = useStyles();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [fetchParameter, setFetchParameter] = useState(initialFetchParameters);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const searchFieldRef = useRef(null);

  /// the local handles for various callbacks
  /// to minimize rerendering
  /// as well as managing some state locally so that the consumer of this grid needs minimum code
  const localFetchHandle = useCallback(
    async (args) => {
      try {
        const newSearchParameter = { ...fetchParameter, ...args };
        setFetchParameter(newSearchParameter);
        setIsLoading(true);
        const { newRows, newPagination, newLoading, newError } =
          await fetchCallBack(newSearchParameter);
        setPagination(newPagination);
        setRows(newRows);
        setIsError(newError);
        setIsLoading(newLoading);
      } catch (e) {
        console.warn(e);
        setIsError(e);
      } finally {
        setIsLoading(false);
      }
    },

    [fetchCallBack, fetchParameter]
  );

  const localDeleteHandle = useCallback(
    (id, name, messageParts) => {
      if (deleteCallBack) {
        if (showWarning) {
          modalInfo = {
            type: 'deletion',
            pageCodeText: pageCodeText,
            data: messageParts,
            modalConfirmationFunction: () =>
              deleteCallBack(id, name, messageParts, setModalOpen)
          };
          setModalOpen(true);
        } else {
          deleteCallBack(id, name, messageParts);
        }
      }
    },

    [deleteCallBack, pageCodeText, showWarning]
  );

  const localActivationHandle = useCallback(
    (id, checked, name, messageParts) => {
      if (activationCallBack) {
        if (showWarning) {
          modalInfo = {
            type: checked ? 'activation' : 'suspension',
            pageCodeText: pageCodeText,
            data: messageParts,
            modalConfirmationFunction: () =>
              activationCallBack(id, checked, name, messageParts, setModalOpen)
          };
          setModalOpen(true);
        } else {
          activationCallBack(id, checked, name, messageParts);
        }
      }
    },

    [activationCallBack, pageCodeText, showWarning]
  );

  const localDetailHandle = useCallback(
    (id) => {
      detailCallBack(id);
    },
    [detailCallBack]
  );

  /// the initialiFetchParameters are significant in the sense that it includes configurations that are NOT
  /// settable fron the grid's perspective (which will be kept for the entire life cyle of one Grid instance)
  /// because only the calling page has such information, same applies for columns and rows setup
  useEffect(() => {
    localFetchHandle(initialFetchParameters);
    // eslint-disable-next-line
  }, []);

  const onSearchClear = (e) => {
    localFetchHandle({ keywords: '' });
    setSearchText('');

    if (searchFieldRef.current) {
      searchFieldRef.current.focus();
    }
  };

  const nakedSearchHandler = (e) => {
    const searchInput = e.target.value;
    setSearchText(searchInput);

    if (searchInput.length > 1 || searchInput.length === 0)
      // the API dictated that the keyword has to be at least two characters, or nothing (no keyword filter)
      localFetchHandle({ keywords: searchInput });
  };

  // the accumulated user input only causes one server poll every half a second
  const debouncedSearchHandler = useDebounce(nakedSearchHandler, 500);
  /// Attaching additional behaviors to marked columns
  const enhancedColumns = visibleColumns.map((col) => {
    if (col.isToggle) {
      return {
        ...col,
        ...(editMode && {
          width: Math.max(col.width || 0, EDIT_STATUS_COLUMN_WIDTH),
          minWidth: Math.max(col.minWidth || 0, EDIT_STATUS_COLUMN_WIDTH)
        }),
        renderCell: (cellParams) => (
          <ActivationAndDeleteButton cellParams={cellParams} />
        )
      };
    }
    return col;
  });

  //////////////////////////////////////////////////////
  /// rendering the activate/delete stuff for one row///
  //////////////////////////////////////////////////////

  const constuctWarningMessageParts = (cellParams) => {
    const calculatedFields = Object.keys(cellParams.row).filter((k) =>
      k.startsWith('$')
    );
    const messageParts = calculatedFields.map((k) => {
      const colDef = visibleColumns.find((el) => el.field === k);
      return { label: colDef.headerName, value: cellParams.row[k] };
    });
    return messageParts;
  };

  const ActivationAndDeleteButton = ({ cellParams }) => {
    const messageParts = constuctWarningMessageParts(cellParams);
    const entityName = cellParams.row.name;
    const id = cellParams.id;
    const active = cellParams.value;
    const markedForDeletion = deletionChanges.hasOwnProperty(id);
    const oneSwitchCheckedStatus = activationChanges.hasOwnProperty(id)
      ? activationChanges[id].checked
      : active;
    const oneSwitchStatusText = oneSwitchCheckedStatus ? 'Active' : 'Inactive';
    const isSystem = cellParams.row.is_system; // doesn't matter if this column doesn't exist

    if (
      editMode &&
      !markedForDeletion &&
      isSystem &&
      systemRowActionMessage
    )
      return (
        <div className={classes.systemRowStatus}>
          {oneSwitchStatusText}
          <ITooltip
            arrow
            placement='top'
            content={systemRowActionMessage}
          >
            <Chip
              aria-label={systemRowActionMessage}
              className={classes.systemRowChip}
              label={systemRowLabel}
              size='small'
              variant='outlined'
            />
          </ITooltip>
        </div>
      );

    if (editMode && !markedForDeletion)
      return (
        <>
          {oneSwitchStatusText}
          <Switch
            sx={activationSwitchStyle(theme)}
            disabled={isSystem}
            checked={oneSwitchCheckedStatus}
            onClick={(e) => {
              localActivationHandle(
                id,
                e.target.checked,
                entityName,
                messageParts
              );
            }}
          />
          <GarbageBinIconButton
            disabled={isSystem}
            onClick={() => {
              localDeleteHandle(id, entityName, messageParts);
            }}
          />
        </>
      );

    if (editMode && markedForDeletion)
      return (
        <div className={classes.deletionStatus}>
          <Switch
            sx={activationSwitchStyle(theme)}
            checked={active}
            disabled
          />
          <span>Marked for deletion</span>
          <ITooltip arrow placement='top' content='Undo deletion'>
            <IconButton
              aria-label={`Undo deletion for ${entityName}`}
              size='small'
              onClick={() => {
                deleteCallBack(id, entityName, messageParts);
              }}
            >
              <UndoIcon fontSize='small' />
            </IconButton>
          </ITooltip>
        </div>
      );

    // not even in edit mode
    return oneSwitchStatusText;
  };

  /**
   * Callback to re-fetch data on page changes
   * @param {number} page Page index
   */
  const onPageChange = (page) => {
    localFetchHandle({ page });
  };
  //////////////////////////////////////////////////////////
  /// end rendering the activate/delete stuff for one row///
  //////////////////////////////////////////////////////////

  /// the main render function for the grid
  return (
    <div style={{ height: '90%', width: '100%' }}>
      {modalOpen && (
        <ThreeStageModal
          type={modalInfo.type}
          pageCodeText={modalInfo.pageCodeText}
          open={modalOpen}
          setOpen={setModalOpen}
          confirmFunction={modalInfo.modalConfirmationFunction}
          viewDataItems={modalInfo.data}
        />
      )}
      <div className={classes.searchFieldWrapper}>
        <SearchField
          inputRef={searchFieldRef}
          searchFieldClassName={classes.searchField}
          onChange={debouncedSearchHandler}
          onClear={onSearchClear}
          defaultValue={searchText}
        />
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <DataGrid
          sx={dataGridStyle(theme)}
          className={classes.root}
          sortModel={sortModel}
          error={isError}
          disableColumnMenu
          sortingOrder={['asc', 'desc']} // the default x-datagrid sorting order array has a third state of null, not good when we only want to support sorting by one column
          rowCount={pagination.total}
          rows={rows}
          columns={enhancedColumns}
          components={{
            Footer: (props) => (
              <CustomFooter
                {...props}
                pagination={pagination}
                onPageChange={onPageChange}
              />
            ),
            NoResultsOverlay: () => <NoData error={isError} />,
            NoRowsOverlay: () => <NoData error={isError} />
          }}
          isLoading={isLoading}
          filterMode={'server'}
          paginationMode={'server'}
          sortingMode={'server'}
          rowsPerPageOptions={[20, 40, 60, 80, 100]}
          pageSize={pagination.per_page}
          page={pagination.page}
          onPageChange={(newPage) => {
            localFetchHandle({ page: newPage });
          }}
          onPageSizeChange={(newSize) =>
            localFetchHandle({
              page: DEFAULT_PAGINATION.page,
              per_page: newSize
            })
          }
          onSortModelChange={(sortModel) => {
            setSortModel(sortModel);
            const sortDirection = sortModel[0].sort === 'asc' ? '+' : '-';
            const sortField = sortModel[0].field;
            const sortParameter = sortDirection + sortField;
            localFetchHandle({
              sort: sortParameter,
              page: DEFAULT_PAGINATION.page
            });
          }}
          onFilterModelChange={(e) => alert(JSON.stringify(e))}
          onRowClick={(e) => {
            if (!editMode) localDetailHandle(e.id);
          }}
          //hideFooter//
          // hideFooterPagination
          slots={{
            toolbar: GridToolbar,

            columnSortedDescendingIcon: ArrowDropDownIcon,
            columnSortedAscendingIcon: ArrowDropUpIcon
          }}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDataGrid-columnHeaders': {
      background: theme.palette.secondary.accent
    }
  },
  searchField: {
    boxShadow: '1px 1px 5px 0px #00000040;',
    border: '1px solid'
  },
  searchFieldWrapper: {
    marginBottom: '20px'
  },
  systemRowStatus: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px'
  },
  systemRowChip: {
    fontSize: '11px',
    height: '24px'
  },
  deletionStatus: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    whiteSpace: 'nowrap',
    '& > *': {
      flexShrink: 0
    }
  }
}));

const dataGridStyle = (theme) => ({
  width: '100%',
  height: '633px',
  border: 'none',
  '.MuiDataGrid-sortIcon': {
    color: 'white'
  },
  '.MuiDataGrid-columnSeparator': {
    display: 'none'
  },
  '.MuiDataGrid-columnHeaders': {
    height: '45px',
    color: 'white',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: '4px',
    width: '100%',
    border: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  '.MuiDataGrid-columnHeader': {
    paddingLeft: '12px',
    textAlign: 'start',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    border: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  '.MuiDataGrid-row': {
    background: reExportedColors.TABLE_ROW, ////// HARDCODED TO USE CONSTS - to be Assigned a palette color
    height: '40px',
    maxHeight: '45px',
    color: reExportedColors.TEXT, ////// HARDCODED TO USE CONSTS - to be Assigned a palette color
    fontSize: '11px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: '4px',
    width: '100%',
    border: 'none',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  '.MuiDataGrid-cell': {
    fontSize: '13px',
    paddingLeft: '16px',
    fontFamily: 'Roboto, sans-serif',
    color: reExportedColors.TEXT,
    border: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  '.MuiDataGrid-footerContainer': {
    border: 'none'
  },
});

export default React.memo(GenericGrid);
